// src/services/api.js  –  All API calls with per-client auth token
// Includes: Part 3 (ledger) + Part 4 (fuelBalance) + Part 6 (audit) + Part 8 (banks/financials)

const BASE = 'https://fuelstation.norissolutions.com/api';

function getToken() {
  try {
    const u = localStorage.getItem('pg_user');
    return u ? JSON.parse(u).token : '';
  } catch (_) { return ''; }
}

async function req(path, method = 'GET', body = null) {

  const activeStationId =
    localStorage.getItem('pg_station_id') || '';

  const opts = {
    method,
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'X-Station-Id': activeStationId,
    },
  };

  if (body instanceof FormData) {

    opts.body = body;

  } else if (body) {

    opts.headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, opts);

  if (res.status === 401) {

    localStorage.removeItem('pg_user');
    window.location.href = '/';
    return;
  }

  const json = await res.json();

  if (!json.success) {
    throw new Error(json.message || 'API error');
  }

  return json.data;
}

async function reqBlob(path) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Authorization': `Bearer ${getToken()}` }
  });
  if (res.status === 401) {
    localStorage.removeItem('pg_user');
    window.location.href = '/';
    return;
  }
  if (!res.ok) throw new Error('Download failed');
  return res.blob();
}

const get = (p) => req(p);
const post = (p, b) => req(p, 'POST', b);
const put = (p, b) => req(p + (p.includes('?') ? '&' : '?') + '_method=PUT', 'POST', b);
const patch = (p, b) => req(p + (p.includes('?') ? '&' : '?') + '_method=PATCH', 'POST', b);
const del = (p) => req(p + (p.includes('?') ? '&' : '?') + '_method=DELETE', 'POST');

export const api = {
  // ── Auth ──────────────────────────────────────────────────
  auth: {
    register: (d) => post('/auth.php?action=register', d),
    login: (d) => post('/auth.php?action=login', d),
    logout: () => post('/auth.php?action=logout', {}),
    me: () => get('/auth.php?action=me'),
    forgotPassword: (d) => post('/forgot.php', d),
    resetPassword: (d) => post('/reset-password.php', d),
  },

  // ── Dashboard ─────────────────────────────────────────────
  dashboard: { get: (date) => get(`/dashboard.php?date=${date}`) },

  // ── Products ──────────────────────────────────────────────
  products: {
    list: () => get('/products.php'),
    fetch: (id) => get(`/products.php?id=${id}`),
    create: (d) => post('/products.php', d),
    update: (id, d) => put(`/products.php?id=${id}`, d),
    remove: (id) => del(`/products.php?id=${id}`),
  },

  // ── Tanks ─────────────────────────────────────────────────
  tanks: {
    list: (all) => get('/tanks.php' + (all ? '?all=1' : '')),
    fetch: (id) => get(`/tanks.php?id=${id}`),
    create: (d) => post('/tanks.php', d),
    update: (id, d) => put(`/tanks.php?id=${id}`, d),
    // NOTE: direct current_stock writes are intentionally unsupported — stock is
    // the opening balance at creation and thereafter moves only through stock
    // transactions (Dip Reading, Fuel Balancing, Purchases). The backend rejects
    // a current_stock PATCH (409) to keep the stock ledger accurate.
    toggleStatus: (id, active) => patch(`/tanks.php?id=${id}`, { is_active: active }),
    remove: (id) => del(`/tanks.php?id=${id}`),
  },

  // ── Pumps ─────────────────────────────────────────────────
  pumps: {
    list: () => get('/pumps.php'),
    create: (d) => post('/pumps.php', d),
    update: (id, d) => put(`/pumps.php?id=${id}`, d),
    remove: (id) => del(`/pumps.php?id=${id}`),
    toggleStatus: (id, s) => patch(`/pumps.php?id=${id}`, { status: s }),
  },

  // ── Nozzles ───────────────────────────────────────────────
  nozzles: {
    list: (all) => get('/nozzles.php' + (all ? '?all=1' : '')),
    create: (d) => post('/nozzles.php', d),
    update: (id, d) => put(`/nozzles.php?id=${id}`, d),
    toggleStatus: (id, active) => patch(`/nozzles.php?id=${id}`, { is_active: active }),
    remove: (id) => del(`/nozzles.php?id=${id}`),
  },

  // ── Customers ─────────────────────────────────────────────
  customers: {
    list: () => get('/customers.php'),
    creditList: () => get('/customers.php?type=Credit'),
    fetch: (id) => get(`/customers.php?id=${id}`),
    create: (d) => post('/customers.php', d),
    update: (id, d) => put(`/customers.php?id=${id}`, d),
    remove: (id) => del(`/customers.php?id=${id}`),
  },

  // ── Suppliers ─────────────────────────────────────────────
  suppliers: {
    list: () => get('/suppliers.php'),
    create: (d) => post('/suppliers.php', d),
    update: (id, d) => put(`/suppliers.php?id=${id}`, d),
    remove: (id) => del(`/suppliers.php?id=${id}`),
  },

  // ── Meter Readings ────────────────────────────────────────
  meterReadings: {
    template: (date, shift) => get(`/meter_readings.php?action=template&date=${date}&shift=${shift}`),
    list: (date) => get(`/meter_readings.php${date ? '?date=' + date : ''}`),
    fetch: (id) => get(`/meter_readings.php?id=${id}`),
    create: (d) => {
      const hasImages = d.items && d.items.some(item => item.image_file || item.image);
      if (hasImages) {
        const fd = new FormData();
        fd.append('header[reading_date]', d.reading_date || '');
        fd.append('header[shift_no]', d.shift_no || 1);

        const cleanItems = d.items.map(item => {
          const { image_file, image, ...rest } = item;
          return rest;
        });
        fd.append('items', JSON.stringify(cleanItems));

        d.items.forEach(item => {
          const file = item.image_file || item.image;
          if (file) {
            fd.append(`nozzle_image_${item.nozzle_id}`, file);
          }
        });
        return post('/meter_readings.php', fd);
      }
      return post('/meter_readings.php', d);
    },
    update: (id, d) => {
      const hasImages = d.items && d.items.some(item => item.image_file || item.image);
      if (hasImages) {
        const fd = new FormData();
        if (d.reading_date) fd.append('header[reading_date]', d.reading_date);
        if (d.shift_no) fd.append('header[shift_no]', d.shift_no);

        const cleanItems = d.items.map(item => {
          const { image_file, image, ...rest } = item;
          return rest;
        });
        fd.append('items', JSON.stringify(cleanItems));

        d.items.forEach(item => {
          const file = item.image_file || item.image;
          if (file) {
            fd.append(`nozzle_image_${item.nozzle_id}`, file);
          }
        });
        return put(`/meter_readings.php?id=${id}`, fd);
      }
      return put(`/meter_readings.php?id=${id}`, d);
    },
    remove: (id) => del(`/meter_readings.php?id=${id}`),
    // (Part 15) Download a nozzle's photo for fullscreen preview.
    // Mirrors purchases.downloadAttachment exactly — same auth, same
    // blob handling. Used by MeterReading.jsx when r.file_path is set.
    downloadItemAttachment: (itemId) =>
      reqBlob(`/meter_readings.php?action=file&item_id=${itemId}`),
    getStartDate: () => get('/meter_readings.php?action=get_start_date'),
    updateStartDate: (dt) => post('/meter_readings.php?action=update_start_date', { meter_start_date: dt }),
  },

  // ── Stock Entries ─────────────────────────────────────────
  stockEntries: {
    list: (dt, t) => get(`/stock_entries.php?date=${dt || ''}&tank=${t || ''}`),
    create: (d) => post('/stock_entries.php', d),
    update: (id, d) => put(`/stock_entries.php?id=${id}`, d),
    remove: (id) => del(`/stock_entries.php?id=${id}`),
  },

  // ── Sales ─────────────────────────────────────────────────
  sales: {
    list: (dt, cid, q) => get(`/sales.php?date=${dt || ''}&customer_id=${cid || ''}&q=${q || ''}`),
    create: (d) => post('/sales.php', d),
    update: (id, d) => put(`/sales.php?id=${id}`, d),
    cancel: (id) => patch(`/sales.php?id=${id}`, {}),
    remove: (id) => del(`/sales.php?id=${id}`),
  },

  // ── Lube Sales ────────────────────────────────────────────
  lubeSales: {
    list: (dt) => get(`/lube_sales.php${dt ? '?date=' + dt : ''}`),
    fetch: (id) => get(`/lube_sales.php?id=${id}`),
    create: (d) => post('/lube_sales.php', d),
    update: (id, d) => put(`/lube_sales.php?id=${id}`, d),
    remove: (id) => del(`/lube_sales.php?id=${id}`),
  },

  // ── Credit Sales ──────────────────────────────────────────
  creditSales: {
    summary: (params) => get('/credit_sales.php', params),
    byCustomer: (id) => get(`/credit_sales.php?customer_id=${id}`),
    byDate: (date) => get(`/credit_sales.php?date=${date}`),
    create: (d) => post('/credit_sales.php', d),
    update: (id, d) => put(`/credit_sales.php?id=${id}`, d),
    remove: (id) => del(`/credit_sales.php?id=${id}`),
    collect: (id, amt, mode) => patch(`/credit_sales.php?id=${id}`, { pay_amount: amt, mode }),
  },

  // ── Product Rates ────────────────────────────────────────
  productRates: {
    list: () => get('/product_rates.php'),
    create: (d) => post('/product_rates.php', d),
    history: (code) => get(`/product_rates.php?action=history&code=${code}`),
    remove: (id) => del(`/product_rates.php?id=${id}`),
  },

  // ── Purchases ─────────────────────────────────────────────
  purchases: {
    list: (dt, s) => get(`/purchases.php?date=${dt || ''}&status=${s || ''}`),
    create: (d) => post('/purchases.php', d),
    update: (id, d) => put(`/purchases.php?id=${id}`, d),
    receive: (id) => patch(`/purchases.php?id=${id}`, { status: 'Received' }),
    remove: (id) => del(`/purchases.php?id=${id}`),
    downloadAttachment: (id) => reqBlob(`/purchases.php?action=download&id=${id}`),
  },

  purchaseReturns: {
    list: () => get('/purchase_returns.php'),
    create: (d) => post('/purchase_returns.php', d),
    update: (id, d) => put(`/purchase_returns.php?id=${id}`, d),
    remove: (id) => del(`/purchase_returns.php?id=${id}`),
  },

  reconciliation: {
    getSummary: (date) => get(`/reconciliation.php?date=${date}`),
    closeDay: (d) => post('/reconciliation.php', d),
    reset: (d) => post('/reconciliation.php?action=reset', d),
    // Day-End Summary Report (header + sections A–H, aggregated totals).
    report: (date) => get(`/reconciliation.php?action=report&date=${date}`),
  },

  // ── Dip Readings ──────────────────────────────────────────
  dipReadings: {
    list: (dt) => get(`/dip_readings.php${dt ? '?date=' + dt : ''}`),
    all: () => get('/dip_readings.php'),
    create: (d) => post('/dip_readings.php', d),
    remove: (id) => del(`/dip_readings.php?id=${id}`),
  },

  // ── Daily Receipts ────────────────────────────────────────
  dailyReceipts: {
    list: (dt) => get(`/daily_receipts.php${dt ? '?date=' + dt : ''}`),
    create: (d) => post('/daily_receipts.php', d),
    update: (id, d) => put(`/daily_receipts.php?id=${id}`, d),
    remove: (id) => del(`/daily_receipts.php?id=${id}`),
    downloadAttachment: (id) => reqBlob(`/daily_receipts.php?action=download&id=${id}`),
  },

  // ── Bill Receipts ─────────────────────────────────────────
  billReceipts: {
    list: (dt, cid) => get(`/bill_receipts.php?date=${dt || ''}&customer_id=${cid || ''}`),
    create: (d) => post('/bill_receipts.php', d),
    update: (id, d) => put(`/bill_receipts.php?id=${id}`, d),
    remove: (id) => del(`/bill_receipts.php?id=${id}`),
  },

  // ── Item Loss ─────────────────────────────────────────────
  itemLoss: {
    list: (dt) => get(`/item_loss.php${dt ? '?date=' + dt : ''}`),
    create: (d) => post('/item_loss.php', d),
    update: (id, d) => put(`/item_loss.php?id=${id}`, d),
    remove: (id) => del(`/item_loss.php?id=${id}`),
  },

  // ── Tickets ───────────────────────────────────────────────
  tickets: {
    list: () => get('/tickets.php'),
    fetch: (id) => get(`/tickets.php?id=${id}`),
    create: (d) => post('/tickets.php', d),
    reply: (id, d) => post(`/tickets.php?action=reply&id=${id}`, d),
    updateStatus: (id, s) => patch(`/tickets.php?id=${id}`, { status: s }),
  },

  // ── Ledger / Accounting (Part 3) ──────────────────────────
  ledger: {
    daybook: (f, t) => get(`/ledger.php?action=daybook&from=${f}&to=${t}`),
    cashbook: (dt) => get(`/ledger.php?action=cashbook&date=${dt}`),
    customerOutstanding: () => get('/ledger.php?action=customer_outstanding'),
    customerLedger: (id) => get(`/ledger.php?action=customer_ledger&customer_id=${id}`),
    supplierOutstanding: () => get('/ledger.php?action=supplier_outstanding'),
    supplierLedger: (id) => get(`/ledger.php?action=supplier_ledger&supplier_id=${id}`),
    trialBalance: (asOf) => get(`/ledger.php?action=trial_balance&as_of=${asOf}`),
  },

  // ── Fuel Balancing (Part 4) ───────────────────────────────
  fuelBalance: {
    compute: (dt) => get(`/fuel_balance.php?action=compute&date=${dt}`),
    range: (f, t) => get(`/fuel_balance.php?action=range&from=${f}&to=${t}`),
    logs: (f, t) => get(`/fuel_balance.php?action=logs&from=${f}&to=${t}`),
    logEntry: (d) => post('/fuel_balance.php?action=log', d),
  },

  // ── Audit Log (Part 6 — admin only) ───────────────────────
  audit: {
    list: ({ from, to, table = '', act = '' } = {}) => {
      const p = new URLSearchParams();
      if (from) p.set('from', from);
      if (to) p.set('to', to);
      if (table) p.set('table', table);
      if (act) p.set('act', act);
      return get(`/audit_logs.php?${p}`);
    },
    stats: (f, t) => get(`/audit_logs.php?action=stats&from=${f}&to=${t}`),
    remove: (id) => del(`/audit_logs.php?id=${id}`),
    bulkRemove: (ids) => post('/audit_logs.php?action=bulk_delete', { ids }),
  },

  // ── Banks (Part 8) ────────────────────────────────────────
  banks: {
    list: () => get('/banks.php?action=list'),
    statement: (bid, f, t) => get(`/banks.php?action=statement&bank_id=${bid}&from=${f}&to=${t}`),
    create: (d) => post('/banks.php', d),
    update: (id, d) => put(`/banks.php?id=${id}`, d),
    setDefault: (id) => patch(`/banks.php?id=${id}&action=set_default`, {}),
    remove: (id) => del(`/banks.php?id=${id}`),
  },

  // ── Financials (Part 8) ───────────────────────────────────
  financials: {
    pnl: (f, t) => get(`/financials.php?action=pnl&from=${f}&to=${t}`),
    balanceSheet: (asOf) => get(`/financials.php?action=balance_sheet&as_of=${asOf}`),
    summary: (f, t, td) => get(`/financials.php?action=summary&from=${f || ''}&to=${t || ''}&today=${td || ''}`),
  },

  // ── Payments (Part 10) ────────────────────────────────────
  payments: {
    list: (dt) => get(`/payments.php${dt ? '?date=' + dt : ''}`),
    fetch: (id) => get(`/payments.php?id=${id}`),
    create: (d) => post('/payments.php', d),
    remove: (id) => del(`/payments.php?id=${id}`),
    // Per-employee Salary History / Advance History (payment_type: salary|advance)
    history: (employeeId, paymentType) =>
      get(`/payments.php?employee_id=${employeeId}&payment_type=${paymentType}`),
  },

  // ── Expenses (Part 10) ────────────────────────────────────
  expenses: {
    list: (f, t, cid) => get(`/expenses.php?from=${f || ''}&to=${t || ''}&category_id=${cid || ''}`),
    create: (d) => post('/expenses.php', d),
    update: (id, data) => put(`/expenses.php?id=${id}`, data),
    remove: (id) => del(`/expenses.php?id=${id}`),
    categories: () => get('/expenses.php?action=categories'),
    addCategory: (n) => post('/expenses.php?action=add_category', { name: n }),
    downloadAttachment: (id) => reqBlob(`/expenses.php?action=download&id=${id}`),
  },

  // ── Chat / Business Assistant (Part 10) ───────────────────
  chat: {
    ask: (m) => post('/chat.php', { message: m }),
  },

  // ── Global search (Part 10)
  search: (q) => get(`/search.php?q=${encodeURIComponent(q)}`),

  // ── Users & Roles (Part 2 — admin only) ───────────────────
  users: {
    list: () => get('/users.php'),
    create: (d) => post('/users.php', d),
    update: (id, d) => put(`/users.php?id=${id}`, d),
    remove: (id) => del(`/users.php?id=${id}`),
    resetPassword: (id, pw) => post(`/users.php?action=reset_password&id=${id}`, { password: pw }),
  },

  roles: {
    list: () => get('/roles.php'),
    catalog: () => get('/roles.php?action=catalog'),
    fetch: (id) => get(`/roles.php?id=${id}`),
    create: (d) => post('/roles.php', d),
    update: (id, d) => put(`/roles.php?id=${id}`, d),
    remove: (id) => del(`/roles.php?id=${id}`),
  },

  // ── Contra (cash/bank transfers) ──────────────────────────
  contra: {
    list: () => get('/contra.php'),
    fetch: (id) => get(`/contra.php?id=${id}`),
    create: (d) => post('/contra.php', d),
    remove: (id) => del(`/contra.php?id=${id}`),
  },

  // ── Journal Vouchers (admin manual ledger) ────────────────
  journal: {
    list: () => get('/journal.php'),
    fetch: (id) => get(`/journal.php?id=${id}`),
    create: (d) => post('/journal.php', d),
    remove: (id) => del(`/journal.php?id=${id}`),
  },

  // ── Extended Financial Statements ─────────────────────────
  financialsPlus: {
    cashFlow: (from, to) => get(`/financials_plus.php?action=cash_flow&from=${from}&to=${to}`),
    trialBalance: (asOf) => get(`/financials_plus.php?action=trial_balance&as_of=${asOf}`),
    agedReceivables: () => get('/financials_plus.php?action=aged_receivables'),
    agedPayables: () => get('/financials_plus.php?action=aged_payables'),
  },

  // ── Stock Ledger ──────────────────────────────────────────
  stockLedger: {
    tanks: () => get('/stock_ledger.php?action=tanks'),
    list: (from, to, tankId) => {
      let url = `/stock_ledger.php?from=${from}&to=${to}`;
      if (tankId) url += `&tank_id=${tankId}`;
      return get(url);
    },
    regenerate: (d) => post('/stock_ledger.php?action=regenerate', d),
    setOpening: (d) => post('/stock_ledger.php?action=set_opening', d),
    update: (id, d) => patch(`/stock_ledger.php?id=${id}`, d),
    // Transaction-level per-tank ledger (purchases/sales/testing/evap), built
    // from source tables by tank_id. type: ''|PURCHASE|SALE|TESTING|EVAP.
    tankLedger: (tankId, from, to, type) => {
      let url = `/stock_ledger.php?action=tank_ledger&tank_id=${tankId}&from=${from}&to=${to}`;
      if (type) url += `&type=${encodeURIComponent(type)}`;
      return get(url);
    },
  },

  // ── Employees ─────────────────────────────────────────────
  employees: {
    list: () => get('/employees.php'),
    fetch: (id) => get(`/employees.php?id=${id}`),
    create: (d) => post('/employees.php', d),
    update: (id, d) => put(`/employees.php?id=${id}`, d),
    remove: (id) => del(`/employees.php?id=${id}`),
  },

  // ── Overtime ──────────────────────────────────────────────
  overtime: {
    list: (status) => get(`/overtime.php${status ? '?status=' + status : ''}`),
    fetch: (id) => get(`/overtime.php?action=one&id=${id}`),
    create: (d) => post('/overtime.php', d),
    update: (id, d) => put(`/overtime.php?id=${id}`, d),
    approve: (id) => patch(`/overtime.php?id=${id}&action=approve`, {}),
    reject: (id, reason) => patch(`/overtime.php?id=${id}&action=reject`, { reason }),
    pay: (id, d) => patch(`/overtime.php?id=${id}&action=pay`, d),
    remove: (id) => del(`/overtime.php?id=${id}`),
  },

  // ── Ledgers (Chart of Accounts) ──────────────────────────
  ledgers: {
    list: (type, active) => {
      const params = [];
      if (type) params.push(`type=${encodeURIComponent(type)}`);
      if (active !== undefined) params.push(`active=${active ? 1 : 0}`);
      const qs = params.length ? '?' + params.join('&') : '';
      return get(`/ledgers.php${qs}`);
    },
    fetch: (id) => get(`/ledgers.php?id=${id}`),
    transactions: (id, from, to) => {
      let url = `/ledgers.php?action=transactions&id=${id}`;
      if (from) url += `&from=${from}`;
      if (to) url += `&to=${to}`;
      return get(url);
    },
    create: (d) => post('/ledgers.php', d),
    update: (id, d) => put(`/ledgers.php?id=${id}`, d),
    remove: (id) => del(`/ledgers.php?id=${id}`),
  },

  // ── Ledger-based financial reports (Part 15) ─────────────
  financialsLedger: {
    trialBalance: (asOf) => get(`/financials_ledger.php?action=trial_balance&as_of=${asOf}`),
    profitLoss: (from, to) => get(`/financials_ledger.php?action=profit_loss&from=${from}&to=${to}`),
    balanceSheet: (asOf) => get(`/financials_ledger.php?action=balance_sheet&as_of=${asOf}`),
    cashFlow: (from, to) => get(`/financials_ledger.php?action=cash_flow&from=${from}&to=${to}`),
  },

  // ── Stations (Multi-station support) ──────────────────────
  stations: {
    mine: () => get('/stations.php?action=mine'),
    current: () => get('/stations.php?action=current'),
    register: (d) => post('/stations.php?action=register', d),
    pending: () => get('/stations.php?action=pending'),
    all: (status) => get('/stations.php?action=all' + (status ? `&status=${status}` : '')),
    approve: (id) => put(`/stations.php?action=approve&id=${id}`, {}),
    reject: (id, note) => put(`/stations.php?action=reject&id=${id}`, { note }),
  },
};

export default api;
